import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Container, Dot, OverflowContainer } from './styles';
import { getDisplayedIndexes } from '@spa/shared/components/Carousel';

const DIRECTION_LEFT = -1;
const DIRECTION_RIGHT = 1;
const VISIBLE_DOTS_NUMBER = 5;
const TRANSITION_VALUE = 0.2;

const dotsConfig = {
  size: 6,
  margin: 3,
  number: VISIBLE_DOTS_NUMBER
};

const DOT_SIZE = dotsConfig.size + dotsConfig.margin * 2;

const calculateTranslateValue = (step = 1) => -1 * (DOT_SIZE * step);

const ScrollIndicator = ({ count, activeIndex }) => {
  const [translateValue, setTranslateValue] = useState(0);
  const [transitionValue, setTransitionValue] = useState(0.2);
  const [internalActiveIndex, setInternalActiveIndex] = useState(0);
  const visibleNumber = count > 5 ? VISIBLE_DOTS_NUMBER : count;

  const dots = [...new Array(visibleNumber)].map((_, index) => index);
  const [visibleDots, setVisibleDots] = useState(dots.slice(0, visibleNumber));

  const isDotShrunken = index => {
    const dots = visibleDots.length === 7 ? visibleDots.slice(1, 6) : visibleDots;
    const isFirstOrLastOfAllDots = index === 0 || index === count - 1;
    const isFirstOrLastOfVisibleDots = [dots[0], dots[dots.length - 1]].includes(index);

    return !dots.includes(index) || (isFirstOrLastOfVisibleDots && !isFirstOrLastOfAllDots);
  };

  useEffect(() => {
    const shouldRefreshVisibleDots = activeIndex < count - 2 && activeIndex > 2;

    setTransitionValue(0);
    if (shouldRefreshVisibleDots) {
      setVisibleDots(getDisplayedIndexes(count, activeIndex, 7));
      setTranslateValue(calculateTranslateValue());

      return;
    }

    setVisibleDots(
      activeIndex >= count - 2
        ? [...new Array(visibleNumber)].map((_, index) => count - 1 - index).reverse()
        : [...new Array(visibleNumber)].map((_, index) => index)
    );
    setTranslateValue(0);
  }, [internalActiveIndex]);

  useEffect(() => {
    if (count < 6) {
      return;
    }

    const step = activeIndex - internalActiveIndex > 0 ? 1 : 0;
    const shouldRefreshVisibleDots = activeIndex < count - 2 && activeIndex > 2;
    const direction = activeIndex - internalActiveIndex > 0 ? DIRECTION_RIGHT : DIRECTION_LEFT;

    if (!shouldRefreshVisibleDots && !(activeIndex === 2 && direction === DIRECTION_LEFT)) {
      setInternalActiveIndex(activeIndex);

      return;
    }

    setTransitionValue(TRANSITION_VALUE);

    const multiplier = (activeIndex === 3 ? 1 : 2) * direction;

    setTranslateValue(calculateTranslateValue(step) * multiplier);
    const timeout = setTimeout(() => setInternalActiveIndex(activeIndex), 250);

    return () => clearTimeout(timeout);
  }, [activeIndex]);

  return (
    <OverflowContainer>
      <Container translateValue={translateValue} transitionValue={transitionValue}>
        {visibleDots.map(value => {
          const classNames = [
            ...(isDotShrunken(value) ? ['shrunken'] : []),
            ...(value === activeIndex ? ['active'] : [])
          ].join(' ');

          return <Dot className={classNames} key={value} config={dotsConfig} />;
        })}
      </Container>
    </OverflowContainer>
  );
};

ScrollIndicator.propTypes = {
  count: PropTypes.number.isRequired,
  activeIndex: PropTypes.number.isRequired
};

export default ScrollIndicator;
